import { apiClient } from '@/lib/apiClient';

export interface CreditBalance {
  balance: number;
  lowBalanceThreshold: number;
}

export interface CreditPack {
  id: string;
  code: string;
  name: string;
  description: string | null;
  credits: number;
  priceCents: number;
  currency: string;
}

export interface CheckoutResponse {
  redirectUrl: string;
  externalSessionId: string;
}

export async function fetchBalance(): Promise<CreditBalance> {
  const { data } = await apiClient.get<CreditBalance>('/me/credits/balance');
  return data;
}

export interface DailyBurnPoint {
  date: string;
  debits: number;
  credits: number;
}

export interface CategoryBurn {
  category: string;
  debits: number;
}

export interface UsageAnalytics {
  balance: number;
  days: number;
  daily: DailyBurnPoint[];
  byCategory: CategoryBurn[];
  estimatedRunwayDays: number | null;
}

export async function fetchUsage(days = 30): Promise<UsageAnalytics> {
  const { data } = await apiClient.get<UsageAnalytics>('/billing/usage', {
    params: { days },
  });
  return data;
}

export interface SubscriptionTier {
  code: string;
  name: string;
  description: string | null;
  monthlyPriceCents: number | null;
  bundledCreditsMonthly: number;
  maxUsers: number | null;
  maxAgents: number | null;
  maxConcurrentWorkflows: number | null;
}

export async function fetchTiers(): Promise<SubscriptionTier[]> {
  const { data } = await apiClient.get<SubscriptionTier[]>('/billing/tiers');
  return data;
}

export async function fetchMyTier(): Promise<SubscriptionTier> {
  const { data } = await apiClient.get<SubscriptionTier>('/billing/tiers/me');
  return data;
}

export interface AutoTopupSettings {
  enabled: boolean;
  packId: string | null;
  lowBalanceThreshold: number;
  paymentMethodOnFile: boolean;
}

export async function fetchAutoTopup(): Promise<AutoTopupSettings> {
  const { data } = await apiClient.get<AutoTopupSettings>('/billing/auto-topup');
  return data;
}

export async function updateAutoTopup(req: {
  enabled: boolean;
  packId: string | null;
  lowBalanceThreshold: number;
}): Promise<AutoTopupSettings> {
  const { data } = await apiClient.put<AutoTopupSettings>('/billing/auto-topup', req);
  return data;
}

export async function fetchPacks(): Promise<CreditPack[]> {
  const { data } = await apiClient.get<CreditPack[]>('/billing/packs');
  return data;
}

export async function startCheckout(
  packCode: string,
  successUrl?: string,
  cancelUrl?: string,
): Promise<CheckoutResponse> {
  const { data } = await apiClient.post<CheckoutResponse>('/billing/checkout', {
    packCode,
    successUrl,
    cancelUrl,
  });
  return data;
}

export function formatPrice(priceCents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(priceCents / 100);
}
